import { v4 as uuidv4 } from 'uuid';

import { API_BASE_URL, DEFAULT_HEADERS } from '../../apiConfig';

// ✅ Validate required fields
const validateCourseData = (courseData) => {
  const required = [
    'title',
    'description',
    'objectives',
    'priorKnowledge',
    'difficultyLevel',
    'weeklyHours',
    'lessons',
  ];
  const missing = required.filter((field) => !courseData[field]);
  if (missing.length > 0) {
    throw new Error(`Missing required fields: ${missing.join(', ')}`);
  }

  if (parseInt(courseData.lessons) <= 0 || parseInt(courseData.lessons) > 20) {
    throw new Error('Number of lessons must be between 1 and 20');
  }

  return true;
};

// ✅ Ensure consistent formatting
const cleanCourseData = (courseData) => ({
  ...courseData,
  objectives: Array.isArray(courseData.objectives)
    ? courseData.objectives
    : courseData.objectives.split(',').map((obj) => obj.trim()),
  priorKnowledge: Array.isArray(courseData.priorKnowledge)
    ? courseData.priorKnowledge
    : courseData.priorKnowledge.split(',').map((pre) => pre.trim()),
  referenceBooks: Array.isArray(courseData.referenceBooks)
    ? courseData.referenceBooks
    : (courseData.referenceBooks || '')
        .split(',')
        .map((book) => book.trim())
        .filter(Boolean),
  weeklyHours: parseInt(courseData.weeklyHours),
  lessons: parseInt(courseData.lessons),
});

const normalizeNodeId = (value, index) => {
  const base = String(value || '').trim();
  if (base) return base;
  return `topic_${index + 1}`;
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const ensureUuid = (value) => {
  const candidate = String(value || '').trim();
  return UUID_RE.test(candidate) ? candidate : uuidv4();
};

const normalizeKnowledgeGraph = (payload) => {
  const rawNodes =
    payload?.knowledgeGraph || payload?.courseGraph || payload?.graph || payload?.nodes || [];

  if (!Array.isArray(rawNodes)) return [];

  return rawNodes.map((node, index) => {
    const id = normalizeNodeId(node?.id, index);
    return {
      id,
      title: node?.title || `Topic ${index + 1}`,
      summary: node?.summary || node?.description || '',
      prerequisites: Array.isArray(node?.prerequisites)
        ? node.prerequisites.map((p) => String(p))
        : [],
      keyPoints: Array.isArray(node?.keyPoints) ? node.keyPoints : [],
      examples: Array.isArray(node?.examples) ? node.examples : [],
      order: typeof node?.order === 'number' ? node.order : index,
      deepDivePrompt: node?.deepDivePrompt || '',
    };
  });
};

const normalizeUnits = (payload, knowledgeGraph) => {
  const rawUnits = payload?.units || payload?.lessonPlan?.units || payload?.course?.units || [];
  const isGenericUnitLabel = (value) =>
    /^((unit|lesson|topic)\s*\d+)$/i.test(String(value || '').trim());

  const cleanUnitTitle = (value) =>
    String(value || '')
      .trim()
      .replace(/^unit\s+\d+\s*[:.-]?\s*/i, '');

  const pickUnitTitle = (unit, graphNode, index) => {
    const candidates = [
      unit?.unit_title,
      unit?.lesson_title,
      unit?.lessonTitle,
      unit?.name,
      unit?.topic,
      graphNode?.title,
      unit?.title,
    ]
      .map(cleanUnitTitle)
      .filter(Boolean)
      .filter((title) => !isGenericUnitLabel(title));

    return candidates[0] || `Lesson ${index + 1}`;
  };

  if (Array.isArray(rawUnits) && rawUnits.length > 0) {
    return rawUnits.map((unit, index) => {
      const graphNode = knowledgeGraph[index] || {};
      return {
        id: unit?.id || graphNode.id || `unit_${index + 1}`,
        title: pickUnitTitle(unit, graphNode, index),
        objective: unit?.objective || unit?.unit_objective || graphNode.summary || '',
        assessment: unit?.assessment || unit?.unit_assessment || '',
        position: typeof unit?.position === 'number' ? unit.position : index,
        kgTopicId: unit?.kgTopicId || graphNode.id || null,
        deepDivePrompt: unit?.deepDivePrompt || graphNode.deepDivePrompt || '',
      };
    });
  }

  return knowledgeGraph.map((node, index) => ({
    id: node.id,
    title: node.title,
    objective: node.summary,
    assessment: '',
    position: typeof node.order === 'number' ? node.order : index,
    kgTopicId: node.id,
    deepDivePrompt: node.deepDivePrompt || '',
  }));
};

const normalizeProfileText = (value, fallback) => {
  if (Array.isArray(value)) {
    const joined = value
      .map((item) => String(item).trim())
      .filter(Boolean)
      .join(', ');
    return joined || fallback;
  }

  const normalized = String(value ?? '').trim();
  return normalized || fallback;
};

const normalizeCourseResponse = (payload, cleaned, profileData) => {
  const root = payload?.course || payload;
  const knowledgeGraph = normalizeKnowledgeGraph(root);
  const units = normalizeUnits(root, knowledgeGraph);

  return {
    id: ensureUuid(root?.id || root?.courseId),
    title: root?.title || cleaned.title,
    description: root?.description || cleaned.description,
    objectives: Array.isArray(root?.objectives) ? root.objectives : cleaned.objectives,
    priorKnowledge: Array.isArray(root?.priorKnowledge)
      ? root.priorKnowledge
      : Array.isArray(root?.prior_knowledge)
        ? root.prior_knowledge
        : cleaned.priorKnowledge,
    difficultyLevel: root?.difficultyLevel || root?.difficulty_level || cleaned.difficultyLevel,
    weeklyHours:
      root?.weeklyHours ||
      root?.weekly_hours ||
      cleaned.weeklyHours ||
      parseInt(cleaned.weeklyHours),
    lessons: units.length || cleaned.lessons,
    referenceBooks: Array.isArray(root?.referenceBooks)
      ? root.referenceBooks
      : Array.isArray(root?.reference_books)
        ? root.reference_books
        : cleaned.referenceBooks,
    createdAt: root?.createdAt || new Date().toISOString(),
    status: root?.status || 'active',
    progress: typeof root?.progress === 'number' ? root.progress : 0,
    currentLessonIndex: typeof root?.currentLessonIndex === 'number' ? root.currentLessonIndex : 0,
    units,
    knowledgeGraph,
    metadata: {
      ...(root?.metadata || {}),
      generationMode: 'knowledge_graph',
      studentProfile: {
        name: normalizeProfileText(profileData?.name, 'Anonymous'),
        educationLevel: normalizeProfileText(
          profileData?.education_level ?? profileData?.educationLevel,
          'Not specified'
        ),
        interests: normalizeProfileText(profileData?.interests, 'Not specified'),
        careerGoals: normalizeProfileText(
          profileData?.career_goals ?? profileData?.careerGoals,
          'Not specified'
        ),
      },
    },
  };
};

const buildLearnerProfile = (profileData) => ({
  name: normalizeProfileText(profileData?.name, 'Anonymous'),
  educationLevel: normalizeProfileText(
    profileData?.education_level ?? profileData?.educationLevel,
    'Not specified'
  ),
  careerGoals: normalizeProfileText(
    profileData?.career_goals ?? profileData?.careerGoals,
    'Not specified'
  ),
  interests: normalizeProfileText(profileData?.interests, 'Not specified'),
  certifications: normalizeProfileText(profileData?.certifications, 'None'),
});

export const CourseDetailsService = {
  async generateCourse(courseData, profileData) {
    // Defensive: apply placeholder defaults to all fields
    profileData = profileData || {};
    courseData = courseData || {};
    // ✅ Validation
    validateCourseData(courseData);
    const cleaned = cleanCourseData(courseData);
    // Defensive: ensure cleaned is always an object
    if (!cleaned || typeof cleaned !== 'object') {
      throw new Error('Invalid course data after cleaning');
    }

    const learnerProfile = buildLearnerProfile(profileData);
    const payload = {
      courseRequest: {
        title: cleaned.title ?? 'Untitled Course',
        topic: cleaned.title ?? 'Untitled Course',
        learnerQuestion: cleaned.description ?? 'No course description provided.',
        objectives: cleaned.objectives ?? ['No objectives provided.'],
        priorKnowledge: cleaned.priorKnowledge ?? ['None'],
        difficultyLevel: cleaned.difficultyLevel ?? 'Unknown',
        weeklyHours: cleaned.weeklyHours ?? 1,
        targetLessonCount: cleaned.lessons ?? 1,
        referenceBooks: cleaned.referenceBooks ?? [],
      },
      learnerProfile,
      generationPolicy: {
        mode: 'KNOWLEDGE_GRAPH',
        requireTopicAnchoring: true,
        requireObjectiveCoverage: true,
        requirePrerequisiteOrdering: true,
        personalization: {
          interestsRole: 'examples_only',
          maxInterestInfluence: 0.25,
          doNotReplaceCoreTopic: true,
        },
      },
    };

    const tryRequest = async (url, body) =>
      fetch(`${API_BASE_URL}${url}`, {
        method: 'POST',
        headers: DEFAULT_HEADERS,
        body: JSON.stringify(body),
      });

    const legacyPayload = {
      course: {
        title: cleaned.title,
        description: cleaned.description,
        objectives: cleaned.objectives,
        priorKnowledge: cleaned.priorKnowledge,
        difficultyLevel: cleaned.difficultyLevel,
        weeklyHours: cleaned.weeklyHours,
        lessons: cleaned.lessons,
        referenceBooks: cleaned.referenceBooks,
      },
      profile: payload.learnerProfile,
    };

    const legacyPayloadAlt = {
      title: cleaned.title,
      description: cleaned.description,
      objectives: cleaned.objectives,
      priorKnowledge: cleaned.priorKnowledge,
      difficultyLevel: cleaned.difficultyLevel,
      weeklyHours: cleaned.weeklyHours,
      lessons: cleaned.lessons,
      referenceBooks: cleaned.referenceBooks,
      learnerProfile,
    };

    let res = await tryRequest('/generate-course-graph', payload);
    if (!res.ok) {
      res = await tryRequest('/generate-course', legacyPayload);
    }
    if (!res.ok) {
      res = await tryRequest('/generate-course', legacyPayloadAlt);
    }
    if (!res.ok) {
      const errPayload = await res.json().catch(() => null);
      throw new Error(errPayload?.error || `Failed to generate course: ${res.statusText}`);
    }

    const data = await res.json();
    return normalizeCourseResponse(data, cleaned, profileData);
  },

  async generateCourseFromPrompt(prompt, profileData) {
    const cleanedPrompt = String(prompt || '').trim();
    if (!cleanedPrompt) {
      throw new Error('Course prompt is required');
    }

    const learnerProfile = buildLearnerProfile(profileData);
    const response = await fetch(`${API_BASE_URL}/course-creation/generate`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify({
        prompt: cleanedPrompt,
        learnerProfile,
      }),
    });

    if (!response.ok) {
      const errPayload = await response.json().catch(() => null);
      throw new Error(errPayload?.error || `Failed to generate course: ${response.statusText}`);
    }

    const data = await response.json();
    const inferredCourseRequest = data?.courseRequest || data?.course_request || {};
    const resolvedObjectives = [
      inferredCourseRequest.objectives,
      inferredCourseRequest.learningObjectives,
      data?.objectives,
      ['Apply the course prompt in practice'],
    ].find(Boolean);
    const resolvedPriorKnowledge = [
      inferredCourseRequest.priorKnowledge,
      inferredCourseRequest.prior_knowledge,
      data?.priorKnowledge,
      [],
    ].find(Boolean);
    const normalizedCourseRequest = {
      title: inferredCourseRequest.title || data?.title || cleanedPrompt.slice(0, 80),
      description:
        inferredCourseRequest.description ||
        inferredCourseRequest.learnerQuestion ||
        data?.description ||
        cleanedPrompt,
      objectives: resolvedObjectives,
      priorKnowledge: resolvedPriorKnowledge,
      difficultyLevel:
        inferredCourseRequest.difficultyLevel ||
        inferredCourseRequest.difficulty_level ||
        data?.difficultyLevel ||
        'Beginner',
      weeklyHours:
        inferredCourseRequest.weeklyHours ||
        inferredCourseRequest.weekly_hours ||
        data?.weeklyHours ||
        3,
      lessons:
        inferredCourseRequest.lessons ||
        inferredCourseRequest.targetLessonCount ||
        data?.lessons ||
        6,
      referenceBooks:
        inferredCourseRequest.referenceBooks ||
        inferredCourseRequest.reference_books ||
        data?.referenceBooks ||
        [],
    };

    return normalizeCourseResponse(data, cleanCourseData(normalizedCourseRequest), profileData);
  },
};
